import type {CSSProperties, ReactNode} from 'react';
import React from 'react';

import CSSModules from 'react-css-modules';

import HintBox from '../../HintBox';
import Heading from '../../TextComponents/Heading';

import styles from './styles.module.scss';

type Hint = {
  title: string;
  description: string;
  icon?: ReactNode;
  offset?: number;
};

type CommonProps = {
  children: ReactNode;
  bubbleTitle?: string;
  compact?: boolean;
  overlayNumber?: number;
  hint?: Hint;
  showTriangle?: boolean;
  reskinClassName?: string;
  dataAutomation?: string;
};

type ConditionalHoverableProps =
  | {
      type: 'hoverable';
      onMouseEnter: () => void;
      onMouseLeave: () => void;
    }
  | {
      type: 'static' | 'danger';
      onMouseEnter?: () => void;
      onMouseLeave?: () => void;
    };

type BubbleProps = CommonProps & ConditionalHoverableProps;

const Bubble = ({
  children,
  type = 'static',
  bubbleTitle,
  compact,
  overlayNumber = 1,
  hint,
  showTriangle = true,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  reskinClassName = '',
  dataAutomation,
}: BubbleProps): JSX.Element => {
  // eslint-disable-next-line complexity
  const getContents = () => {
    const style = hint && hint.offset ? {right: -hint.offset} : {};
    const hintBox = hint && (
      <div styleName='bubble-hint' style={style}>
        <HintBox
          title={hint.title}
          text={hint.description}
          icon={hint.icon}
          showButton={false}
          showCheckBox={false}
        />
      </div>
    );

    if (children instanceof Array && children.length === 2) {
      return (
        <div>
          {hintBox}
          <div styleName='bubble-top'>{children[0]}</div>
          <div styleName='bubble-bottom'>{children[1]}</div>
        </div>
      );
    } else {
      const styleName = compact ? 'bubble-compact' : 'bubble';
      const rows: ReactNode[] = [];
      for (let i = 0; i < overlayNumber; i++) {
        const top = 20 * i + 'px';
        const left = 15 * i + 'px';
        const zIndex = overlayNumber - i;
        const rowStyle: CSSProperties = {
          zIndex,
          width: '100%',
          height: '100%',
          top,
          left,
          position: i === 0 ? 'relative' : 'absolute',
        };
        rows.push(
          <div
            key={i}
            styleName={i === 0 ? styleName : 'bubbleBackground'}
            style={rowStyle}
          >
            {i === 0 && children}
          </div>
        );
      }
      const rowsContainerStyle = {
        marginBottom: overlayNumber > 1 ? 20 * overlayNumber + 'px' : 0,
      };
      return (
        <div styleName='rowsContainer' style={rowsContainerStyle}>
          {hintBox}
          {rows}
        </div>
      );
    }
  };

  const triangle = (
    <div styleName='triangleContainer'>
      <div styleName='triangle-border' />
      <div styleName='triangle' />
    </div>
  );

  return (
    <div styleName={`bubbleTitleContainer ${reskinClassName}`}>
      {bubbleTitle && (
        <div styleName='headingContainer'>
          <Heading as='h3' size='cat' weight='medium' colour='grey300'>
            {bubbleTitle}
          </Heading>
        </div>
      )}
      <div
        styleName={`bubbleContainer ${type !== 'static' ? type : ''}`}
        data-automation={dataAutomation}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {getContents()}
        {showTriangle && triangle}
      </div>
    </div>
  );
};

export default CSSModules(Bubble, styles, {allowMultiple: true});
